import React, { FC, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Timestamp } from 'api/models';
import { Project } from 'api/project';
import { TimesheetCalendarSourceType } from 'api/timesheets';
import { Category } from 'api/timesheets/categories';
import { Configuration } from 'api/timesheets/configuration/configuration';
import { Calendar } from 'api/timesheets/integration/calendar';
import { CalendarIntegration } from 'api/timesheets/integration/calendar-integration-provider';
import { ReactComponent as GoogleCalenderIcon } from 'assets/svgs/google-calendar.svg';
import { ReactComponent as OutlookCalenderIcon } from 'assets/svgs/outlook.svg';
import { Button } from 'components/atoms/button';
import { DrawerContent } from 'components/organisms/timesheets/common/drawer';
import { weekFormatter } from 'components/organisms/timesheets/common/utils';
import { useTimesheetCalendarEventsQuery } from 'queries/timesheets/calendar-integration-actions';
import { Content } from 'stores/core';
import {
  TimesheetCalendarIntegrationEntry,
  TimesheetWeeklyRow,
} from '../../../types';
import {
  Drawer,
  DrawerBody,
  DullCaption,
  Footer,
  TopSelection,
} from '../component';
import { Profile } from '../profile';
import { getSourceTypeFromTimesheetConfig } from '../util';
import { SelectedActivityRowData } from './activity-table';
import { AddActivity } from './add-activity';
import { Wrapper } from './component';
import { useGetTimesheetCalenderConfig } from './hooks';
import { SelectCalender } from './select-calendar';

interface SelectEventPageProps {
  integration: CalendarIntegration;
  endDate: Timestamp;
  startDate: Timestamp;
  onClose: () => void;
  visible: boolean;
  addIntegrationTaskToTimesheet: (
    task: TimesheetCalendarIntegrationEntry[],
  ) => void;
  projects?: Project[];
  timesheetWeekRows?: TimesheetWeeklyRow[];
  configuration: Configuration;
  timeEntryCategories: Category[];
}

const ButtonGroup = styled.div<{ $apart?: boolean }>`
  display: flex;
  justify-content: ${($apart) => ($apart ? 'space-between' : 'default')};
  gap: var(--spacing-04);
  ${Button} {
    flex: ${($apart) => ($apart ? 0 : 1)};
  }
`;

export const AddActivityPage: FC<SelectEventPageProps> = ({
  integration,
  endDate,
  startDate,
  onClose,
  visible,
  addIntegrationTaskToTimesheet,
  projects,
  timesheetWeekRows,
  configuration,
  timeEntryCategories,
}) => {
  const { integrationId } = integration;
  const [selectedCalendar, setSelectedCalender] = useState<Content<Calendar>>([
    'loading',
  ]);
  const isGcal =
    getSourceTypeFromTimesheetConfig(configuration) ===
    TimesheetCalendarSourceType.GoogleCalendar;

  const [selectedRow, setSelectedRow] = useState<SelectedActivityRowData[]>();
  const {
    data: eventsWithMeta,
    refetch,
    isLoading,
    isFetching,
  } = useTimesheetCalendarEventsQuery({
    endDate,
    startDate,
    calendarId: selectedCalendar[1]?.calendarId,
    integrationId,
  });

  const { addTaskListToTimesheet, rowData, isUpdating } =
    useGetTimesheetCalenderConfig({
      addIntegrationTaskToTimesheet,
      eventsWithMeta,
      projects,
      timesheetWeekRows,
    });

  useEffect(() => {
    if (!visible) {
      return;
    }
    refetch();
  }, [visible, refetch]);

  return (
    <Drawer
      visible={visible}
      onClose={onClose}
      destroyOnClose
      title={
        <Wrapper>
          {isGcal ? <GoogleCalenderIcon /> : <OutlookCalenderIcon />}
          <div>
            <FormattedMessage id="timesheets.integration.drawer.title" />
            <DullCaption>{weekFormatter(startDate)}</DullCaption>
          </div>
        </Wrapper>
      }
    >
      <DrawerContent.Content>
        <DrawerBody>
          <TopSelection>
            <SelectCalender
              onChange={setSelectedCalender}
              selection={selectedCalendar}
              integrationId={integrationId}
            />
            <Profile integration={integration} />
          </TopSelection>
          <AddActivity
            onSelect={setSelectedRow}
            eventsWithMeta={rowData}
            isLoading={
              isLoading || selectedCalendar[0] === 'loading' || isFetching
            }
            startDate={startDate}
            refresh={refetch}
            projects={projects}
            mandateCategory={configuration.configMeta.categoryMandatory}
            timeEntryCategories={timeEntryCategories}
          />
        </DrawerBody>
      </DrawerContent.Content>
      <DrawerContent.Footer>
        <Footer>
          <ButtonGroup $apart>
            <Button
              iconPlacement="right"
              type="ghost"
              onClick={() => refetch()}
            >
              <FormattedMessage id="refresh" />
            </Button>
            <ButtonGroup>
              <Button type="ghost" onClick={onClose}>
                <FormattedMessage id="cancel" />
              </Button>
              <Button
                disabled={!selectedRow?.length}
                loading={isUpdating}
                type="primary"
                onClick={() => {
                  if (!selectedRow?.length) {
                    return;
                  }
                  addTaskListToTimesheet(selectedRow);
                }}
              >
                <FormattedMessage
                  id="timesheets.buttons.add_adhoc_activity_with_count"
                  values={{
                    count: selectedRow?.length ? selectedRow?.length : 0,
                  }}
                />
              </Button>
            </ButtonGroup>
          </ButtonGroup>
        </Footer>
      </DrawerContent.Footer>
    </Drawer>
  );
};
